import React, { useEffect, useState, useCallback } from 'react';
import { Input, Table, DropdownMenu, Spinner } from '@veda/dashboard-ui';
import { MdBadge, MdPersonAdd, MdClose, MdMailOutline } from 'react-icons/md';
import { usePaApi } from '../services/paApi';
import { useAuthUser } from '../../ppp/hooks/useAuthUser';

const STATO_BADGE = {
  invitato:  { label: 'In attesa', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  accettato: { label: 'Attivo',    cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  revocato:  { label: 'Revocato',  cls: 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
};

function Modale({ aperto, titolo, onChiudi, children }) {
  if (!aperto) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onChiudi(); }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg mt-16">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700">
          <h2 className="font-semibold text-gray-800 dark:text-white">{titolo}</h2>
          <button onClick={onChiudi} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <MdClose size={20} />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function FormInvita({ onInvita, loading }) {
  const [email, setEmail]   = useState('');
  const [errore, setErrore] = useState('');

  const invia = () => {
    if (!email) return;
    setErrore('');
    onInvita({ email }, (msg) => setErrore(msg));
  };

  return (
    <div className="space-y-4">
      <Input label="Email del RUP *" value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="nome.cognome@comune.it" />
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Il RUP riceverà l'invito a operare per l'ente. Una volta accettato, potrà essere nominato sui procedimenti.
      </p>
      {errore && <p className="text-sm text-red-600">{errore}</p>}
      <div className="flex justify-end">
        <button
          onClick={invia}
          disabled={loading || !email}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium transition-colors"
        >
          <MdMailOutline /> {loading ? 'Invio...' : 'Invia invito'}
        </button>
      </div>
    </div>
  );
}

export default function RupEntePage() {
  const api = usePaApi();
  const { can } = useAuthUser();

  const [righe,         setRighe]         = useState([]);
  const [caricamento,   setCaricamento]   = useState(true);
  const [modaleInvita,  setModaleInvita]  = useState(false);
  const [invitoLoading, setInvitoLoading] = useState(false);
  const [msg,           setMsg]           = useState('');

  const carica = useCallback(() => {
    setCaricamento(true);
    api.listRupAssociati({
      onSuccess: (data) => { setRighe(Array.isArray(data) ? data : []); setCaricamento(false); },
      onFormError: () => setCaricamento(false),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const mostraMsg = (testo) => { setMsg(testo); setTimeout(() => setMsg(''), 3000); };

  const handleInvita = (data, onErr) => {
    setInvitoLoading(true);
    api.invitaRup(data, {
      onSuccess: () => {
        setInvitoLoading(false);
        setModaleInvita(false);
        mostraMsg('Invito inviato con successo.');
        carica();
      },
      onFormError: (err) => {
        setInvitoLoading(false);
        onErr?.(err?.errors?.email?.[0] ?? err?.message ?? 'Errore durante l\'invio.');
      },
    });
  };

  const handleRevoca = (associazioneId) => {
    if (!window.confirm('Revocare l\'associazione di questo RUP all\'ente? Non potrà più essere nominato sui procedimenti.')) return;
    api.revocaRup(associazioneId, {
      onSuccess: () => { mostraMsg('Associazione revocata.'); carica(); },
      onFormError: (err) => mostraMsg(err?.message ?? 'Impossibile revocare l\'associazione.'),
    });
  };

  const canInvita = can('ppp.ente.invite_rup');

  if (caricamento && righe.length === 0) {
    return <div className="flex items-center justify-center py-20"><Spinner /></div>;
  }

  return (
    <div className="w-full py-8 px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-400">
            <MdBadge size={22} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800 dark:text-white">RUP dell'ente</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">RUP associati al tuo ente, nominabili sui procedimenti</p>
          </div>
        </div>
        {canInvita && (
          <button
            onClick={() => setModaleInvita(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
          >
            <MdPersonAdd /> Invita RUP
          </button>
        )}
      </div>

      {msg && (
        <div className="mb-4 p-3 rounded-xl bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 text-sm">
          {msg}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
        <Table
          cols={[
            { name: 'nome',   label: 'Nome' },
            { name: 'email',  label: 'Email' },
            { name: 'stato',  label: 'Stato', size: 'w-32' },
            { name: 'azioni', label: '',      size: 'w-10', orderable: 0 },
          ]}
          cells_style={{
            nome: (row) => row.nome || <span className="text-gray-400">—</span>,
            stato: (row) => {
              const b = STATO_BADGE[row.stato] ?? STATO_BADGE.invitato;
              return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${b.cls}`}>{b.label}</span>;
            },
            azioni: (row) => {
              if (!canInvita || row.stato === 'revocato') return null;
              return (
                <DropdownMenu
                  menu_items={[
                    <button key="revoca" onClick={() => handleRevoca(row.id)}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-gray-100 dark:hover:bg-gray-600">
                      <MdClose size={16} /> Revoca associazione
                    </button>,
                  ]}
                />
              );
            },
          }}
          dataset={righe}
        />
      </div>

      <Modale aperto={modaleInvita} titolo="Invita un RUP all'ente" onChiudi={() => setModaleInvita(false)}>
        <FormInvita onInvita={handleInvita} loading={invitoLoading} />
      </Modale>
    </div>
  );
}
